// 261023 E3-P3a — hàm THUẦN của màn «Sổ duyệt» (một bảng cả truyện, chỉ đọc). KHÔNG import antd / react (jest của react-scripts không load được antd v6).
// Ghép từ các hàm thuần của ⑤ (episodeReviewReasons, criticCardOf, droppedEventsView…) — KHÔNG tự suy luận thêm điều gì mới.
import type { CriticFreshness, EpisodeScriptRecord, OutlineViolation } from '../../../types/adaptation';
import type { HandoffPlanPreview, WorkbenchEpisode, WorkbenchView } from '../../../types/adaptationWorkbench';
import type { SeasonReaderCardView } from '../seasonReaderView';
import { autoFixCardOf, type AutoFixCard } from './autoFixView';
import { chainStopLine, criticBlockOf, criticCardOf, sceneLabel } from './criticReceiptView';
import { droppedEventsView } from './droppedSourceView';
import { scriptViolationChipIds } from './assistant/optionsAction';
import { episodeReviewReasons, readerIssuesFor } from './scriptStageView';
import { isPushable } from './handoffBatchView';
import { traceLineOf, type TraceLine } from './localizeTraceView';

// ─── Kiểu ─────────────────────────────────────────────────────────────────

// 3h3 (K6, README A.6): `need` = «Còn cần bạn» (chặn đẩy / máy không làm nổi) · `hint` = «Gợi ý» (không chặn) · `auto` = «Máy đã tự sửa» · «Máy bỏ qua» là nhãn ở hàng Bước 1–4.
export type ReviewFilter = 'need' | 'hint' | 'auto' | 'pass' | 'pushed' | 'all';
export type ReviewGroup = 'need' | 'hint' | 'auto' | 'pass' | 'pushed';
export type CellTone = 'ok' | 'bad' | 'warn' | 'muted';

export interface JudgeCell { label: string; tone: CellTone; tip: string }
export interface ErrorsCell { count: number; scenesText: string }
export type LeakCell =
  | { kind: 'unknown'; tip: string }
  | { kind: 'block'; count: number; tip: string }
  | { kind: 'swap'; replaced: number; tip: string }
  /** `leaks` của kế hoạch đẩy mà KHÔNG nằm trong `sourceNameBlock`: hộp Đẩy sẽ báo «đọc rồi quyết», không chặn. */
  | { kind: 'warn'; count: number; tip: string }
  | { kind: 'none' };
export interface DroppedCell { important: number; minor: number }
export interface ChaptersCell { label: string; tone: CellTone; tip: string }

export type MainButtonKind = 'viewFailed' | 'askAssistant' | 'viewLeak' | 'view';
export interface MainButton { kind: MainButtonKind; label: string; issueIds: string[]; scene: string | null }
export interface RowNote { sign: '!' | '✗'; text: string }

/** `scene` = cảnh lỗi đầu tiên (id thật, để «Mở cảnh»); null = không trỏ được cảnh. */
export interface RowTrace { line: TraceLine; scene: string | null }

export interface ReviewRow {
  ep: number;
  title: string;
  written: 'none' | 'draft' | 'failed';
  judge: JudgeCell | null;
  errors: ErrorsCell | null;
  leak: LeakCell | null;
  dropped: DroppedCell | null;
  chapters: ChaptersCell;
  groups: ReviewGroup[];
  button: MainButton;
  notes: RowNote[];
  /** 261023 E3-P3b1 — thẻ «Máy đã tự sửa» của tập (null = không có gì để nói). */
  fix: AutoFixCard | null;
  /** 261023 E3-P3h4d1 — «lúc viết đã dùng bước ③ nào» cho tập CÓ lỗi cần xem (chỉ đọc; không chứng minh nguyên nhân). null = không có gì để nói / BE cũ. */
  trace: RowTrace | null;
  /** 261025 E3-P3f — máy chủ cho đẩy tập này (BE `eligibleForPush`) VÀ có băm nội dung. BE cũ ⇒ false ⇒ không tick được. */
  pushable: boolean;
  /** 261025 E3-P3f — băm nội dung lúc người xem (gửi lại khi «Duyệt & đẩy»). */
  contentSha?: string;
}

export interface ReviewBookInput {
  workbench: WorkbenchView;
  scripts: EpisodeScriptRecord[];
  reader: SeasonReaderCardView | null;
  handoff: HandoffPlanPreview | 'unknown';
  /** Lý do «Chưa rõ» (hiện trong tooltip cột Tên gốc rò). */
  handoffReason?: string;
}


// ─── Tiện ích nhỏ ─────────────────────────────────────────────────────────

const FILTERS: readonly ReviewFilter[] = ['need', 'hint', 'auto', 'pass', 'pushed', 'all'];
export const DROPPED_ANCHOR_ID = 'section-dropped-source';
export const sceneAnchorId = (scenePlanId: string): string => `reader-scene-pid-${scenePlanId}`;

export interface CriticChip { id: string; scene: string | null; violation: OutlineViolation }

/**
 * Chip `CRITIC_FINDING` (ý kiến giám khảo, mức cảnh báo) còn SỐNG của tập, kèm id chip BE `violation:<chỉ số gốc>:CRITIC_FINDING`.
 * Gộp trùng theo cùng khoá với ⑤ (`gate|message|superseded`) — id lấy từ `scriptViolationChipIds` để KHÔNG lệch với ⑤.
 * ERROR đã được đếm ở `episodeReviewReasons` ⇒ chip mang mức ERROR không tính lại ở đây.
 */
export const criticChipsOf = (violations: ReadonlyArray<OutlineViolation> | null | undefined): CriticChip[] => {
  const list = Array.isArray(violations) ? violations : [];
  const seen = new Set<string>();
  const firstIdx: number[] = [];
  list.forEach((v, idx) => {
    const key = `${v.gate}|${v.message}|${v.superseded === true ? 'S' : ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    firstIdx.push(idx);
  });
  const ids = scriptViolationChipIds(list);
  const out: CriticChip[] = [];
  firstIdx.forEach((idx, k) => {
    const v = list[idx];
    const id = ids[k];
    if (!id || v.gate !== 'CRITIC_FINDING' || v.superseded === true || v.severity === 'ERROR' || (v.kind ?? 'defect') !== 'defect') return;
    const r0 = Array.isArray(v.refs) && typeof v.refs[0] === 'string' ? v.refs[0].trim() : '';
    out.push({ id, scene: r0 || null, violation: v });
  });
  return out;
};

// ─── Từng ô ───────────────────────────────────────────────────────────────

const NO_RECEIPT_TIP = 'Chưa có biên lai giám khảo hợp lệ cho tập này (bản cũ hoặc chưa chấm) — KHÔNG có nghĩa là đạt';
const STALE_SUFFIX = ' · của bản trước';

const judgeOf = (script: EpisodeScriptRecord | undefined, blocked: boolean): JudgeCell => {
  const c = criticBlockOf(script);
  const card = criticCardOf(script);
  if (!c || !card || c.status === 'UNCHECKED') {
    return { label: 'Chưa kiểm được', tone: 'muted', tip: c && card ? `${c.status} — ${card.headline}` : NO_RECEIPT_TIP };
  }
  const tip = `${c.status} — ${card.headline}`;
  let label: string;
  let tone: CellTone;
  if (c.status === 'PASSED') { label = 'Đạt'; tone = 'ok'; }
  else if (c.status === 'REPAIRED') {
    const verified = c.repair?.accepted === true && Array.isArray(c.repair?.findingsAfter);
    if (blocked || (verified && card.criticalLeft > 0)) { label = 'Máy đã sửa · còn lỗi nặng'; tone = 'bad'; }
    // 3e (R1): nhánh chuỗi cũng sửa MAJOR ⇒ hết CRITICAL mà còn MAJOR thì KHÔNG «đạt» (cùng luật với thẻ ⑤).
    else if (verified && c.chain && card.details.remaining.some((f) => f.severity === 'MAJOR')) { label = 'Máy đã sửa · còn lỗi đáng kể'; tone = 'warn'; }
    else if (verified) { label = 'Máy đã sửa · chấm lại đạt'; tone = 'ok'; }
    else return { label: 'Chưa kiểm được', tone: 'muted', tip };   // biên lai không xác nhận bản sửa ⇒ như thẻ ⑤: không xanh
  } else { label = 'Còn lỗi nặng'; tone = 'bad'; }   // REPAIR_REJECTED / UNRESOLVED
  const f: CriticFreshness | undefined = script?.criticFreshness;
  if (f && f !== 'CURRENT') label += STALE_SUFFIX;
  return { label, tone, tip };
};

const CHAPTERS: Record<string, ChaptersCell> = {
  up_to_date: { label: 'Đã đẩy', tone: 'ok', tip: 'Đã đẩy, Chapters khớp bản này' },
  changed_since_handoff: { label: 'Cần đẩy lại', tone: 'warn', tip: 'Đã sửa sau lần đẩy — Chapters chưa có bản mới' },
  unknown_legacy: { label: 'Chưa rõ khớp', tone: 'muted', tip: 'Đã đẩy trước khi có dấu đối chiếu — chưa rõ còn khớp' },
};
const CHAPTERS_NONE: ChaptersCell = { label: 'Chưa đẩy', tone: 'muted', tip: 'Chưa đẩy sang Chapters' };

const leakOf = (ep: number, handoff: ReviewBookInput['handoff'], reason?: string): LeakCell => {
  if (handoff === 'unknown') return { kind: 'unknown', tip: reason ? `Chưa rõ: ${reason}` : 'Chưa lấy được danh sách tên gốc từ máy chủ' };
  const block = (handoff.sourceNameBlock ?? []).find((b) => b.episode === ep);
  if (block && block.terms.length > 0) return { kind: 'block', count: block.terms.length, tip: `${block.terms.length} tên gốc còn sót mà bảng tên mới không thay được — máy chủ sẽ từ chối đẩy` };
  const swap = (handoff.autoSwap ?? []).find((b) => b.episode === ep);
  if (swap && swap.replaced > 0) return { kind: 'swap', replaced: swap.replaced, tip: `Khi đẩy, máy sẽ tự thay ${swap.replaced} chỗ tên gốc bằng tên mới (kịch bản ⑤ giữ nguyên)` };
  const blocked = new Set((block?.terms ?? []).map((t) => t.term));
  const warn = (handoff.leaks ?? []).find((l) => l && l.episode === ep);
  const warnCount = (warn?.terms ?? []).filter((t) => !blocked.has(t.term)).length;
  if (warnCount > 0) return { kind: 'warn', count: warnCount, tip: `${warnCount} tên gốc còn trong chương — đẩy vẫn được, hộp Đẩy sẽ báo để bạn đọc rồi quyết` };
  return { kind: 'none' };
};

const droppedOf = (script: EpisodeScriptRecord | undefined): DroppedCell => {
  const v = droppedEventsView(script?.droppedSourceEvents);
  const rows = v?.rows ?? [];
  const important = rows.filter((r) => r.tone === 'bad' || r.tone === 'warn').length;
  return { important, minor: rows.length - important };
};

/** L14 (3e) — máy KHÔNG lần ngược: finding C2 / issue người đọc không mang tham chiếu tới bản ②/③/④ đã sinh ra cảnh, nên chưa chứng minh được nguyên nhân. */
export const TRACE_BACK_NOTE = 'máy không tự sửa ngược về các bước trước';   // 3h4d1: không hứa «sẽ lần ngược khi chứng minh được» — máy không bao giờ sửa ngược (B10)

/**
 * 3e (L13) — tập còn ĐÍCH của nhánh chuỗi sau khi máy dừng sửa ⇒ vào «Cần bạn xem» kèm lý do. MAJOR ngoài danh sách trắng (`target:false`) KHÔNG đổi lọc (chỉ hiện như chip hiện nay).
 * Không có `c2Unresolved` (BE cũ / tập ngoài chuỗi) ⇒ `{ need:false, note:null }` — Sổ duyệt y như trước.
 */
export const chainNeedOf = (ep: Pick<WorkbenchEpisode, 'c2Unresolved' | 'c2StopReason'>): { need: boolean; note: string | null } => {
  const n = (ep.c2Unresolved ?? []).filter((u) => u?.target === true).length;
  if (n === 0) return { need: false, note: null };
  return { need: true, note: `${chainStopLine(ep.c2StopReason, n) ?? `Còn ${n} lỗi máy chưa sửa được`} — ${TRACE_BACK_NOTE}` };
};

// ─── Dựng hàng ────────────────────────────────────────────────────────────

const rowOf = (ep: WorkbenchEpisode, script: EpisodeScriptRecord | undefined, input: ReviewBookInput): ReviewRow => {
  const chapters = CHAPTERS[ep.handoff] ?? CHAPTERS_NONE;
  const base = { ep: ep.ep, title: ep.title ?? '', written: ep.written, chapters, pushable: isPushable(ep), ...(ep.contentSha ? { contentSha: ep.contentSha } : {}) };
  if (ep.written === 'none') {
    return { ...base, judge: null, errors: null, leak: null, dropped: null, groups: [], button: { kind: 'view', label: 'Xem', issueIds: [], scene: null }, notes: [], fix: null, trace: null };
  }
  const reasons = episodeReviewReasons(ep, script, readerIssuesFor(input.reader)).filter((r) => r.kind !== 'critic_blocked');
  const chips = criticChipsOf(script?.violations);
  const sceneIds = new Set((script?.script?.scenes ?? []).map((s: { scenePlanId?: string }) => s?.scenePlanId).filter(Boolean) as string[]);
  const scenes = script?.script?.scenes;
  const count = reasons.length + chips.length;
  const sceneRefs: string[] = [];
  chips.forEach((c) => { if (c.scene) { const t = sceneLabel(c.scene, scenes); if (!sceneRefs.includes(t)) sceneRefs.push(t); } });
  const errors: ErrorsCell = { count, scenesText: sceneRefs.join(', ') };

  if (ep.written === 'failed') {
    return {
      ...base, judge: null, errors, leak: null, dropped: null, groups: ['need'],
      button: { kind: 'viewFailed', label: 'Xem lỗi viết', issueIds: [], scene: null },
      notes: [], fix: null, trace: null,
    };
  }

  const blocked = ep.criticBlocked === true;
  const judge = judgeOf(script, blocked);
  const leak = leakOf(ep.ep, input.handoff, input.handoffReason);
  const dropped = droppedOf(script);

  // Ứng viên nút "Nhờ Trợ lý": id chip của lỗi ERROR sống (đúng thứ tự ⑤) rồi chip giám khảo; scene = refs[0] của MỤC ĐẦU nếu là cảnh thật.
  const candidates: Array<{ id: string; ref: string | null }> = [];
  reasons.forEach((r) => {
    if (!r.issueId) return;
    const m = /^violation:(\d+):/.exec(r.issueId);
    const v = m ? script?.violations?.[Number(m[1])] : undefined;
    const r0 = Array.isArray(v?.refs) && typeof v!.refs[0] === 'string' ? v!.refs[0].trim() : '';
    candidates.push({ id: r.issueId, ref: r0 || null });
  });
  chips.forEach((c) => candidates.push({ id: c.id, ref: c.scene }));
  const issueIds: string[] = [];
  candidates.forEach((c) => { if (!issueIds.includes(c.id)) issueIds.push(c.id); });
  const first = candidates[0];
  const scene = first?.ref && sceneIds.has(first.ref) ? first.ref : null;

  const judgeRed = judge.tone === 'bad';
  const unchecked = judge.tone === 'muted';
  const leakBlock = leak.kind === 'block';

  const groups: ReviewGroup[] = [];
  const chainNeed = chainNeedOf(ep);
  // «Còn cần bạn» = chặn đẩy hoặc máy không làm nổi: nối tập bẩn / AI đọc cả mùa chặn / tập ngoài khung (mọi lý do trừ lỗi chấm ERROR), giám khảo còn lỗi nặng, chưa kiểm được, tên gốc rò chặn, máy dừng sửa.
  // Lỗi chấm ERROR còn sống mà KHÔNG chặn đẩy (`eligibleForPush` không xét) chỉ là «Gợi ý». Hàng sạch ⇒ «Đạt».
  const hardReasons = reasons.some((r) => r.kind !== 'critic');
  const hard = hardReasons || judgeRed || blocked || unchecked || leakBlock || chainNeed.need;
  const soft = !hard && count > 0;
  const need = hard || soft;   // «chưa sạch»: không phải «Đạt», không tự tick được
  if (hard) groups.push('need');
  else if (soft) groups.push('hint');
  // 261023 E3-P3b1: tập đang giữ bản máy sửa (BE `autoFix.state === 'current'`) cũng ở tab «Máy đã tự sửa» — kể cả khi chip hiện hành đã là biên lai chấm lại (3b2).
  const fix = autoFixCardOf(ep, script);
  if (!need && (judge.label === 'Máy đã sửa · chấm lại đạt' || fix?.state === 'current')) groups.push('auto');
  if (!need && ep.handoff !== 'up_to_date') groups.push('pass');
  if (ep.handoff === 'up_to_date') groups.push('pushed');

  let button: MainButton;
  if (judgeRed || blocked || issueIds.length > 0) button = { kind: 'askAssistant', label: 'Nhờ Trợ lý sửa lỗi', issueIds, scene };
  else if (leakBlock) button = { kind: 'viewLeak', label: 'Xem tên gốc còn sót', issueIds: [], scene: null };
  else button = { kind: 'view', label: 'Xem', issueIds: [], scene: null };

  const notes: RowNote[] = [];
  if (leak.kind === 'block') notes.push({ sign: '✗', text: `tên gốc rò: đẩy sẽ bị chặn (${leak.count} tên)` });
  if (judgeRed || blocked) {
    const left = criticCardOf(script)?.criticalLeft ?? 0;
    const n = left > 0 ? `còn ${left} lỗi nặng` : 'còn lỗi nặng';
    if (blocked) notes.push({ sign: '✗', text: `${n} — đẩy sẽ bị chặn` });
    else notes.push({ sign: '!', text: `${n} ở bản trước — không chặn đẩy` });
  } else if (unchecked) notes.push({ sign: '!', text: 'Chưa kiểm được' });
  if (chainNeed.note) notes.push({ sign: '!', text: chainNeed.note });

  // 3h4d1: chỉ khi tập CÓ lỗi cần xem và BE đã gửi `localizeTrace` (BE cũ ⇒ null ⇒ hàng y như trước).
  const line = count > 0 ? traceLineOf(script?.localizeTrace, scene) : null;
  const trace: RowTrace | null = line ? { line, scene } : null;
  return { ...base, judge, errors, leak, dropped, groups, button, notes, fix, trace };
};

export const reviewBookRows = (input: ReviewBookInput): ReviewRow[] => {
  const byEp = new Map<number, EpisodeScriptRecord>();
  (input.scripts ?? []).forEach((s) => { if (!byEp.has(s.episode)) byEp.set(s.episode, s); });
  const eps = input.workbench.episodes ?? [];
  const rows = eps.map((e) => rowOf(e, byEp.get(e.ep), input));
  // README A.6: đã đẩy xong mọi tập đã viết ⇒ việc còn lại chỉ là «Gợi ý» (không còn «cần bạn»).
  const written = eps.filter((e) => e.written === 'draft');
  const settled = written.length > 0 && written.every((e) => e.handoff === 'up_to_date');
  return settled ? rows.map((r) => (r.groups.includes('need') ? { ...r, groups: r.groups.map((g) => (g === 'need' ? 'hint' : g)) as ReviewGroup[] } : r)) : rows;
};

export const reviewBookCounts = (rows: ReviewRow[]): Record<ReviewFilter, number> => ({
  need: rows.filter((r) => r.groups.includes('need')).length,
  hint: rows.filter((r) => r.groups.includes('hint')).length,
  auto: rows.filter((r) => r.groups.includes('auto')).length,
  pass: rows.filter((r) => r.groups.includes('pass')).length,
  pushed: rows.filter((r) => r.groups.includes('pushed')).length,
  all: rows.length,
});

export const filterRows = (rows: ReviewRow[], f: ReviewFilter): ReviewRow[] => (f === 'all' ? rows : rows.filter((r) => r.groups.includes(f)));

export type SortKey = 'errors' | 'leak' | 'dropped';
export type SortDir = 'desc' | 'asc';
export interface SortState { key: SortKey; dir: SortDir }

export const cycleSort = (cur: SortState | null, key: SortKey): SortState | null => {
  if (!cur || cur.key !== key) return { key, dir: 'desc' };
  return cur.dir === 'desc' ? { key, dir: 'asc' } : null;
};

const sortValue = (r: ReviewRow, key: SortKey): number | null => {
  if (key === 'errors') return r.errors?.count ?? 0;
  if (key === 'dropped') return r.dropped ? r.dropped.important * 1000 + r.dropped.minor : 0;
  if (r.leak?.kind === 'unknown') return null;
  if (r.leak?.kind === 'block') return 1000 + r.leak.count;   // chặn đẩy xếp trên cảnh báo
  return r.leak?.kind === 'warn' ? r.leak.count : 0;
};

/** "Chưa rõ" luôn cuối (cả hai chiều); bằng nhau thì theo số tập tăng. */
export const sortRows = (rows: ReviewRow[], s: SortState | null): ReviewRow[] => {
  if (!s) return rows;
  const sign = s.dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const va = sortValue(a, s.key);
    const vb = sortValue(b, s.key);
    if (va === null && vb !== null) return 1;
    if (vb === null && va !== null) return -1;
    if (va !== null && vb !== null && va !== vb) return (va - vb) * sign;
    return a.ep - b.ep;
  });
};

export interface StepItem { stage: 'source' | 'skeleton' | 'localize' | 'split'; text: string; blocker: boolean; /** 3g2 (Q11): lỗi cổng MÁY đã tự cho qua ⇒ nhãn «Máy bỏ qua» thay «nên xem». */ machine?: boolean }
/** CHÉP từ BE `MACHINE_SKIPPED_CODE` (`utils/adaptationWorkbench.ts`). */
export const MACHINE_SKIPPED_CODE = 'MACHINE_SKIPPED';
export const MACHINE_SKIPPED_TAG = 'Máy bỏ qua';
const STEP_STAGES = ['source', 'skeleton', 'localize', 'split'] as const;
export const STEPS_CLEAR_TEXT = 'Bước 1–4 không còn việc dở';

export const stepRowItems = (wb: Pick<WorkbenchView, 'stages'>): { items: StepItem[]; clearText: string | null } => {
  const stages = (wb.stages ?? []).filter((s) => (STEP_STAGES as readonly string[]).includes(s.key));
  const items: StepItem[] = [];
  stages.forEach((s) => (s.blockers ?? []).forEach((r) => items.push({ stage: s.key as StepItem['stage'], text: r.message, blocker: true })));
  stages.forEach((s) => (s.attention ?? []).forEach((r) => items.push({ stage: s.key as StepItem['stage'], text: r.message, blocker: false, ...(r.code === MACHINE_SKIPPED_CODE ? { machine: true } : {}) })));
  return { items, clearText: items.length ? null : STEPS_CLEAR_TEXT };
};

export const defaultFilter = (counts: Record<ReviewFilter, number>): ReviewFilter => (counts.need > 0 ? 'need' : counts.hint > 0 ? 'hint' : 'all');
export const parseReviewFilter = (v: string | null | undefined): ReviewFilter | null => (FILTERS as readonly string[]).includes(v ?? '') ? (v as ReviewFilter) : null;

export const emptyFilterText = (f: ReviewFilter, c: Record<ReviewFilter, number>): string => {
  switch (f) {
    case 'need':
      return `Không còn tập nào cần bạn xem — xem tab ${c.pass > 0 ? 'Đạt' : c.pushed > 0 ? 'Đã đẩy' : 'Tất cả'}.`;
    case 'hint': return `Không có gợi ý nào — xem tab ${c.pass > 0 ? 'Đạt' : 'Tất cả'}.`;
    case 'auto': return 'Máy chưa tự sửa tập nào.';
    case 'pass': return `Chưa có tập nào đạt — xem tab ${c.need > 0 ? 'Cần bạn xem' : 'Tất cả'}.`;
    case 'pushed': return `Chưa đẩy tập nào — xem tab ${c.pass > 0 ? 'Đạt' : c.need > 0 ? 'Cần bạn xem' : 'Tất cả'}.`;
    default: return 'Truyện chưa có tập nào.';
  }
};

/** Lối vào ở cột trái: hiện khi có ≥1 tập đã viết; số "cần bạn" tính KHÔNG có tên gốc rò (handoff-plan chỉ tải khi mở Sổ) ⇒ có thể thấp hơn trong Sổ. */
export const reviewEntryView = (wb: WorkbenchView | null, scripts: EpisodeScriptRecord[], reader: SeasonReaderCardView | null): { visible: boolean; needCount: number; hintCount: number } => {
  if (!wb || !(wb.episodes ?? []).some((e) => e.written === 'draft')) return { visible: false, needCount: 0, hintCount: 0 };
  const rows = reviewBookRows({ workbench: wb, scripts, reader, handoff: 'unknown' });
  const c = reviewBookCounts(rows);
  return { visible: true, needCount: c.need, hintCount: c.hint };
};

/** Q5: lý do tiếng Việt khi KHÔNG lấy được handoff-plan (409 / lỗi mạng). Không hiện mã kỹ thuật. */
export const handoffUnknownReason = (err: unknown): string => {
  const res = (err as { response?: { data?: { code?: unknown } } } | null | undefined)?.response;
  if (!res) return 'Không kết nối được máy chủ';
  switch (res.data?.code) {
    case 'OUTLINE_MISSING': return 'Chưa có khung xương nên chưa dò được tên gốc';
    case 'OUTLINE_NOT_APPROVED': return 'Khung xương chưa được duyệt nên chưa dò được tên gốc';
    case 'NO_DRAFT_SCRIPTS': return 'Chưa có kịch bản nháp nào để dò tên gốc';
    case 'EPISODES_STALE': return 'Danh sách tập đã cũ — tải lại trang rồi thử lại';
    default: return 'Chưa lấy được danh sách tên gốc từ máy chủ';
  }
};

// ─── URL ──────────────────────────────────────────────────────────────────

const copy = (p: URLSearchParams): URLSearchParams => new URLSearchParams(p);
const del = (p: URLSearchParams, ...keys: string[]): URLSearchParams => { keys.forEach((k) => p.delete(k)); return p; };

export const isReviewView = (v: string | null | undefined): boolean => v === 'review';

export const enterReviewParams = (prev: URLSearchParams, rf?: ReviewFilter | null): URLSearchParams => {
  const p = del(copy(prev), 'ep', 'from', 'scene', 'open');
  p.set('view', 'review');
  if (rf) p.set('rf', rf);
  return p;
};

export interface OpenEpisodeTarget { ep: number; filter: ReviewFilter; scene?: string | null; openDropped?: boolean }
export const openEpisodeParams = (prev: URLSearchParams, t: OpenEpisodeTarget): URLSearchParams => {
  const p = del(copy(prev), 'view', 'scene', 'open');
  p.set('stage', 'script'); p.set('ep', String(t.ep)); p.set('from', 'review'); p.set('rf', t.filter);
  if (t.scene) p.set('scene', t.scene);
  if (t.openDropped) p.set('open', 'dropped');
  return p;
};

export const backToReviewParams = (prev: URLSearchParams): URLSearchParams => {
  const p = del(copy(prev), 'ep', 'from', 'scene', 'open');
  p.set('view', 'review');
  return p;
};

/** Bấm một chặng: rời Sổ (bỏ view/rf); đổi chặng thì bỏ ep/from/scene/open; cùng chặng ⑤ đang đọc tập thì giữ nguyên. */
export const goToStageParams = (prev: URLSearchParams, stage: string): URLSearchParams => {
  const p = copy(prev);
  const leavingReview = isReviewView(p.get('view'));
  const changed = leavingReview || p.get('stage') !== stage;
  p.delete('view');
  if (changed) del(p, 'rf', 'ep', 'from', 'scene', 'open');
  p.set('stage', stage);
  return p;
};

export const setReviewFilterParams = (prev: URLSearchParams, f: ReviewFilter): URLSearchParams => { const p = copy(prev); p.set('rf', f); return p; };

export interface ReviewOrigin { fromReview: boolean; filter: ReviewFilter | null; scene: string | null; openDropped: boolean }
export const reviewOriginOf = (sp: URLSearchParams): ReviewOrigin => ({
  fromReview: sp.get('from') === 'review',
  filter: parseReviewFilter(sp.get('rf')),
  scene: sp.get('scene') || null,
  openDropped: sp.get('open') === 'dropped',
});
